// TennisTrack Hub — Dettaglio Template Server | 029 Project
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Template.css';

const DettaglioTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { utente } = useAuth();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [forkLoading, setForkLoading] = useState(false);
  const [forkDone, setForkDone] = useState(false);

  // Commenti
  const [commenti, setCommenti] = useState([]);
  const [nuovoCommento, setNuovoCommento] = useState('');
  const [commentoLoading, setCommentoLoading] = useState(false);
  const [commentoErrore, setCommentoErrore] = useState('');

  // Conta template dell'utente per limite free
  const [countMiei, setCountMiei] = useState(0);

  const carica = useCallback(async () => {
    try {
      setLoading(true);
      const [resT, resMiei] = await Promise.all([
        api.get(`/template/server/${id}`),
        api.get('/template/miei')
      ]);
      const t = resT.data.template;
      setTemplate(t);
      setCommenti(t.commenti || []);
      setCountMiei(resMiei.data.template.length);
    } catch (err) {
      setErrore(err.response?.status === 404
        ? 'Template non trovato.'
        : 'Errore nel caricamento.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  const forkTemplate = async () => {
    setForkLoading(true);
    setErrore('');
    try {
      await api.post(`/template/fork/${id}`);
      setForkDone(true);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio.');
    } finally {
      setForkLoading(false);
    }
  };

  const inviaCommento = async (e) => {
    e.preventDefault();
    if (!nuovoCommento.trim()) return;
    setCommentoErrore('');
    setCommentoLoading(true);
    try {
      const res = await api.post(`/template/server/${id}/commenti`, { testo: nuovoCommento });
      setCommenti(prev => [res.data.commento, ...prev]);
      setNuovoCommento('');
    } catch (err) {
      setCommentoErrore(err.response?.data?.error || 'Errore nel commento.');
    } finally {
      setCommentoLoading(false);
    }
  };

  const formatData = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const pianoBadge = (piano) => ({
    demo: <span className="t-badge t-badge-demo">DEMO</span>,
    free: <span className="t-badge t-badge-free">FREE</span>,
    premium: <span className="t-badge t-badge-pro">⭐ PREMIUM</span>
  }[piano]);

  const limiteRaggiunto = utente?.piano === 'free' && countMiei >= 4;

  if (loading) return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main"><div className="loading-msg">Caricamento...</div></main>
    </div>
  );

  if (errore && !template) return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">
        <div className="alert alert-error">{errore}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/template')}>← Torna alla libreria</button>
      </main>
    </div>
  );

  const contenuto = typeof template?.contenuto === 'string'
    ? JSON.parse(template.contenuto || '{}')
    : (template?.contenuto || {});

  const esercizi = contenuto?.esercizi || [];

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header */}
        <div className="page-header">
          <div>
            <button className="btn-back" onClick={() => navigate('/template')}>
              ← Libreria
            </button>
          </div>
        </div>

        {errore && <div className="alert alert-error">{errore}</div>}

        <div className="template-detail">

          {/* Info template */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="template-detail-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.6rem' }}>{template.tipo === 'tennis' ? '🎾' : '💪'}</span>
                <h2 style={{ margin: 0 }}>{template.titolo}</h2>
                {pianoBadge(template.piano)}
                {template.bloccato && <span className="tc-lock">🔒 Solo Pro</span>}
              </div>

              <div className="td-meta-row">
                <span>👤 {template.autore_nome
                  ? `${template.autore_nome} ${template.autore_cognome || ''}`
                  : '029 Project'}</span>
                <span>🏷 {template.livello}</span>
                <span>📥 {template.n_utilizzi} utilizzi</span>
                <span>📅 {formatData(template.created_at)}</span>
                {commenti.length > 0 && <span>💬 {commenti.length} commenti</span>}
              </div>

              {template.descrizione && (
                <p className="td-desc">{template.descrizione}</p>
              )}
            </div>

            {/* Azioni */}
            {!template.bloccato && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {forkDone ? (
                  <div className="alert alert-success" style={{ margin: 0 }}>
                    ✅ Template salvato nella tua libreria!{' '}
                    <span
                      style={{ color: 'var(--color-primary)', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => navigate('/template')}
                    >
                      Vai ai miei template →
                    </span>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={forkTemplate}
                    disabled={forkLoading || limiteRaggiunto}
                    title={limiteRaggiunto ? 'Limite Free raggiunto (max 4)' : ''}
                  >
                    {forkLoading ? 'Salvataggio...' : '💾 Salva nella mia libreria'}
                  </button>
                )}
              </div>
            )}

            {template.bloccato && utente?.piano !== 'pro' && (
              <div className="premium-only-notice">
                <span className="icon">🔒</span>
                <span>Questo template è riservato agli utenti <strong>Pro</strong>. Esegui l'upgrade per accedere al contenuto completo.</span>
              </div>
            )}
          </div>

          {/* Esercizi — solo se non bloccato */}
          {!template.bloccato && esercizi.length > 0 && (
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>
                {template.tipo === 'tennis' ? '🎾' : '💪'} Esercizi ({esercizi.length})
              </h3>
              <div className="esercizi-dettaglio">
                {esercizi.map((es, idx) => (
                  <div key={idx} className="es-card">
                    <div className="es-card-header">
                      <span className="es-num">#{idx + 1}</span>
                      {template.tipo === 'tennis' && es.colpo && (
                        <strong style={{ color: 'var(--text-primary)' }}>{es.colpo}</strong>
                      )}
                      {template.tipo === 'fitness' && es.nome && (
                        <strong style={{ color: 'var(--text-primary)' }}>{es.nome}</strong>
                      )}
                      {template.tipo === 'fitness' && es.tipologia && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>— {es.tipologia}</span>
                      )}
                    </div>

                    {template.tipo === 'tennis' ? (
                      <div className="es-card-body">
                        {es.difficolta && (
                          <div className="es-field" style={{ gridColumn: '1 / -1' }}>
                            <span className="es-label">Difficoltà</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div className="difficolta-bar" style={{ flex: 1 }}>
                                <div
                                  className="difficolta-fill"
                                  style={{ width: `${(es.difficolta / 10) * 100}%` }}
                                />
                              </div>
                              <span className="es-value">{es.difficolta}/10</span>
                            </div>
                          </div>
                        )}
                        {es.serie && (
                          <div className="es-field">
                            <span className="es-label">Serie</span>
                            <span className="es-value">{es.serie}</span>
                          </div>
                        )}
                        {es.ripetizioni && (
                          <div className="es-field">
                            <span className="es-label">Ripetizioni</span>
                            <span className="es-value">{es.ripetizioni}</span>
                          </div>
                        )}
                        {es.durata_min && (
                          <div className="es-field">
                            <span className="es-label">Durata</span>
                            <span className="es-value">{es.durata_min} min</span>
                          </div>
                        )}
                        {es.descrizione && (
                          <div className="es-field" style={{ gridColumn: '1 / -1' }}>
                            <span className="es-label">Descrizione</span>
                            <span className="es-value">{es.descrizione}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="es-card-body">
                        {es.serie && (
                          <div className="es-field">
                            <span className="es-label">Serie</span>
                            <span className="es-value">{es.serie}</span>
                          </div>
                        )}
                        {es.ripetizioni && (
                          <div className="es-field">
                            <span className="es-label">Ripetizioni</span>
                            <span className="es-value">{es.ripetizioni}</span>
                          </div>
                        )}
                        {es.carico_kg && (
                          <div className="es-field">
                            <span className="es-label">Carico</span>
                            <span className="es-value">{es.carico_kg} kg</span>
                          </div>
                        )}
                        {es.note && (
                          <div className="es-field" style={{ gridColumn: '1 / -1' }}>
                            <span className="es-label">Note</span>
                            <span className="es-value">{es.note}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── COMMENTI ── */}
          <div className="card commenti-section">
            <h3>💬 Commenti ({commenti.length})</h3>

            {/* Form commento — solo premium */}
            {utente?.piano === 'pro' ? (
              <form className="commento-form" onSubmit={inviaCommento}>
                {commentoErrore && <div className="alert alert-error" style={{ marginBottom: '10px' }}>{commentoErrore}</div>}
                <textarea
                  placeholder="Scrivi un commento su questo template..."
                  value={nuovoCommento}
                  onChange={e => setNuovoCommento(e.target.value)}
                  maxLength={600}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={commentoLoading || !nuovoCommento.trim()}
                  >
                    {commentoLoading ? 'Invio...' : '📩 Invia commento'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="premium-only-notice">
                <span className="icon">⭐</span>
                <span>Solo gli utenti <strong>Pro</strong> possono commentare i template.</span>
              </div>
            )}

            {/* Lista commenti */}
            {commenti.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Ancora nessun commento. Sii il primo!
              </div>
            ) : (
              <div style={{ marginTop: '16px' }}>
                {commenti.map(c => (
                  <div key={c.id} className="commento-item">
                    <div className="commento-header">
                      <span className="commento-autore">
                        {c.utente_nome} {c.utente_cognome || ''}
                      </span>
                      <span className="commento-data">{formatData(c.created_at)}</span>
                    </div>
                    <p className="commento-testo">{c.testo}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
};

export default DettaglioTemplate;
